import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Package } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import {
  assetCategoriesApi,
  assetLocationsApi,
  assetsApi,
} from '@/services/api/endpoints/assets';
import { departmentsApi } from '@/services/api/endpoints/departments';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { EmployeePicker, type PickedEmployee } from '@/features/hr/EmployeePicker';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import {
  ASSET_CONDITIONS,
  type Asset,
  type AssetCondition,
  type AssetEvent,
  type AssetInput,
  type AssetLocation,
  type AssetStatus,
} from '@/types/asset';
import { formatDate, formatDateTime, orDash } from '@/utils/format';
import {
  ASSET_EVENT_LABEL,
  ASSET_EVENT_TONE,
  AssetConditionBadge,
  AssetStatusBadge,
  Badge,
  Field,
  Money,
  WarrantyBadge,
  humanise,
} from './shared';

/** The named operations. Nothing else moves an asset, changes hands or changes state. */
type Operation =
  | 'assign'
  | 'unassign'
  | 'move'
  | 'condition'
  | 'startMaintenance'
  | 'completeMaintenance'
  | 'lost'
  | 'found'
  | 'dispose';

const OPERATION_LABEL: Record<Operation, string> = {
  assign: 'Assign',
  unassign: 'Return to stock',
  move: 'Move',
  condition: 'Record condition',
  startMaintenance: 'Send for maintenance',
  completeMaintenance: 'Complete maintenance',
  lost: 'Report lost',
  found: 'Mark found',
  dispose: 'Dispose',
};

const OPERATION_DESCRIPTION: Record<Operation, string> = {
  assign: 'To one person or one department. Optionally move it at the same time.',
  unassign: 'Takes it back from its custodian. Optionally say where it now sits.',
  move: 'Where it now is. The history records the move.',
  condition: 'The shape it is in now. The history records the change.',
  startMaintenance: 'Sent for repair or service. It cannot be moved or assigned while away.',
  completeMaintenance:
    'Back from repair. It returns to its custodian if it had one; say what condition it came back in.',
  lost: 'The school cannot say where it is. A reason is required.',
  found: 'Where it turned up and the condition it is in.',
  dispose: 'Final. The record and its history remain, but nothing more can happen to it.',
};

/**
 * Which operations the current status allows. This only decides which
 * buttons to show — the server is the authority and refuses anything else
 * with its own reason, which the error handler shows verbatim.
 */
const OPERATIONS_BY_STATUS: Record<AssetStatus, Operation[]> = {
  IN_STOCK: ['assign', 'unassign', 'move', 'condition', 'startMaintenance', 'lost', 'dispose'],
  IN_USE: ['assign', 'unassign', 'move', 'condition', 'startMaintenance', 'lost', 'dispose'],
  UNDER_MAINTENANCE: ['completeMaintenance', 'lost', 'dispose'],
  LOST: ['found', 'dispose'],
  DISPOSED: [],
};

const DANGEROUS: Operation[] = ['lost', 'dispose'];

/** Local to this screen: the shared query-key registry has no departments entry. */
const ACTIVE_DEPARTMENTS_KEY = ['departments', 'list', { activeOnly: true }] as const;

/** An asset goes to exactly one custodian: a person or a department. */
type AssignTarget = 'EMPLOYEE' | 'DEPARTMENT';

function optional(value: string): string | undefined {
  return value.trim() === '' ? undefined : value.trim();
}

function locationLabel(location: AssetLocation): string {
  return location.campusName === undefined ? location.name : `${location.name} · ${location.campusName}`;
}

/** "A → B", "→ B" or "A →" — whatever halves the server recorded. */
function Change({ label, from, to }: { label: string; from?: string; to?: string }) {
  if (from === undefined && to === undefined) {
    return null;
  }
  return (
    <p className="text-xs text-muted">
      <span className="uppercase tracking-[0.04em] text-subtle">{label}</span>{' '}
      <span className="text-ink">{from ?? '—'}</span>
      <span aria-hidden="true"> → </span>
      <span className="sr-only"> to </span>
      <span className="text-ink">{to ?? '—'}</span>
    </p>
  );
}

function HistoryEntry({ event }: { event: AssetEvent }) {
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Badge tone={ASSET_EVENT_TONE[event.eventType]}>{ASSET_EVENT_LABEL[event.eventType]}</Badge>
        <time dateTime={event.occurredAt} className="text-xs tabular-nums text-subtle">
          {formatDateTime(event.occurredAt)}
        </time>
      </div>
      <div className="mt-1.5 space-y-0.5">
        <Change label="Location" from={event.fromLocation} to={event.toLocation} />
        <Change label="Custodian" from={event.fromCustodian} to={event.toCustodian} />
        <Change
          label="Condition"
          from={event.fromCondition === undefined ? undefined : humanise(event.fromCondition)}
          to={event.toCondition === undefined ? undefined : humanise(event.toCondition)}
        />
        {event.cost !== undefined && (
          <p className="text-xs text-muted">
            <span className="uppercase tracking-[0.04em] text-subtle">Cost</span>{' '}
            <Money value={event.cost} />
          </p>
        )}
        {event.notes !== undefined && (
          <p className="whitespace-pre-line break-words text-sm text-muted">{event.notes}</p>
        )}
      </div>
    </li>
  );
}

/**
 * One asset: what it is, where it is, who has it, and everything that ever
 * happened to it.
 *
 * <p>The operations panel offers only what the current status allows, each in
 * its own form. Location, custody, condition and status never change through
 * the edit form — that carries descriptive fields only, and the server refuses
 * anything else. Reporting lost and disposing ask for a reason and then for a
 * second, explicit confirmation; disposal is final.
 *
 * <p>The warranty verdict is the server's, as are every refusal message and the
 * history — nothing on this page is worked out in the browser.
 */
export function AssetDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.ASSET_MANAGE);
  const seesDepartments = can(PERMISSIONS.DEPARTMENT_VIEW);

  const [operation, setOperation] = useState<Operation | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [assignTarget, setAssignTarget] = useState<AssignTarget>('EMPLOYEE');
  const [employee, setEmployee] = useState<PickedEmployee | null>(null);
  const [departmentId, setDepartmentId] = useState('');
  const [opLocationId, setOpLocationId] = useState('');
  const [opCondition, setOpCondition] = useState<AssetCondition | ''>('');
  const [opCost, setOpCost] = useState('');
  const [opReason, setOpReason] = useState('');
  const [opNotes, setOpNotes] = useState('');

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [vendor, setVendor] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchaseCost, setPurchaseCost] = useState('');
  const [warrantyExpiresOn, setWarrantyExpiresOn] = useState('');
  const [notes, setNotes] = useState('');

  const asset = useQuery({
    queryKey: queryKeys.assets.detail(id),
    queryFn: () => assetsApi.get(id),
  });

  const history = useQuery({
    queryKey: queryKeys.assets.history(id),
    queryFn: () => assetsApi.history(id),
  });

  // Reference data is only needed by the forms, which only managers open.
  const locations = useQuery({
    queryKey: queryKeys.assetReference.locationsAll,
    queryFn: () => assetLocationsApi.list(),
    enabled: manages,
  });

  const categories = useQuery({
    queryKey: queryKeys.assetReference.categories,
    queryFn: () => assetCategoriesApi.list(),
    enabled: manages,
  });

  // Only for the assign form, and only for those who may read departments.
  const departments = useQuery({
    queryKey: ACTIVE_DEPARTMENTS_KEY,
    queryFn: () => departmentsApi.list(true),
    enabled: manages && seesDepartments && operation === 'assign',
  });

  const activeLocations = (locations.data ?? []).filter((row) => row.status === 'ACTIVE');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.assetReference.all });
  };

  function openOperation(next: Operation) {
    setAssignTarget('EMPLOYEE');
    setEmployee(null);
    setDepartmentId('');
    setOpLocationId('');
    setOpCondition(next === 'condition' ? (asset.data?.condition ?? '') : '');
    setOpCost('');
    setOpReason('');
    setOpNotes('');
    setConfirming(false);
    setOperation(next);
  }

  function closeOperation() {
    setOperation(null);
    setConfirming(false);
  }

  const operate = useMutation({
    mutationFn: (op: Operation): Promise<Asset> => {
      const location = opLocationId === '' ? undefined : opLocationId;
      const note = optional(opNotes);
      switch (op) {
        case 'assign':
          // Exactly one custodian: whichever the chosen target names.
          return assetsApi.assign(id, {
            employeeId: assignTarget === 'EMPLOYEE' ? employee?.id : undefined,
            departmentId:
              assignTarget === 'DEPARTMENT' && departmentId !== '' ? departmentId : undefined,
            locationId: location,
            notes: note,
          });
        case 'unassign':
          return assetsApi.unassign(id, location, note);
        case 'move':
          return assetsApi.move(id, { locationId: opLocationId, notes: note });
        case 'condition':
          return assetsApi.changeCondition(id, {
            condition: opCondition as AssetCondition,
            notes: note,
          });
        case 'startMaintenance':
          return assetsApi.startMaintenance(id, note);
        case 'completeMaintenance':
          return assetsApi.completeMaintenance(id, {
            condition: opCondition as AssetCondition,
            cost: opCost.trim() === '' ? undefined : Number(opCost),
            notes: note,
          });
        case 'lost':
          return assetsApi.reportLost(id, opReason.trim());
        case 'found':
          return assetsApi.markFound(id, {
            locationId: opLocationId,
            condition: opCondition as AssetCondition,
            notes: note,
          });
        case 'dispose':
          return assetsApi.dispose(id, opReason.trim());
      }
    },
    onSuccess: (updated, op) => {
      toast.success(`${updated.assetTag}: ${OPERATION_LABEL[op].toLowerCase()} recorded`);
      closeOperation();
      invalidate();
    },
    onError: (error) => {
      handleApiError(error);
      setConfirming(false);
    },
  });

  function openEdit(current: Asset) {
    setName(current.name);
    setCategoryId(current.categoryId);
    setSerialNumber(current.serialNumber ?? '');
    setManufacturer(current.manufacturer ?? '');
    setModel(current.model ?? '');
    setVendor(current.vendor ?? '');
    setPurchaseDate(current.purchaseDate ?? '');
    setPurchaseCost(current.purchaseCost === undefined ? '' : String(current.purchaseCost));
    setWarrantyExpiresOn(current.warrantyExpiresOn ?? '');
    setNotes(current.notes ?? '');
    setEditing(true);
  }

  const save = useMutation({
    mutationFn: () => {
      // Descriptive fields only: location and condition are left out, so they
      // cannot change here — Move and Record condition write the history.
      const payload: AssetInput = {
        name: name.trim(),
        categoryId,
        serialNumber: optional(serialNumber),
        manufacturer: optional(manufacturer),
        model: optional(model),
        vendor: optional(vendor),
        purchaseDate: purchaseDate === '' ? undefined : purchaseDate,
        purchaseCost: purchaseCost.trim() === '' ? undefined : Number(purchaseCost),
        warrantyExpiresOn: warrantyExpiresOn === '' ? undefined : warrantyExpiresOn,
        notes: optional(notes),
      };
      return assetsApi.update(id, payload);
    },
    onSuccess: (updated) => {
      toast.success(`${updated.assetTag} saved`);
      setEditing(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  if (asset.isError) {
    return <ErrorState error={asset.error} onRetry={() => void asset.refetch()} />;
  }
  if (asset.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = asset.data;
  const hasCustodian =
    data.custodianEmployeeId !== undefined || data.custodianDepartmentId !== undefined;
  const available = OPERATIONS_BY_STATUS[data.status].filter(
    // Returning to stock only means something when someone has it.
    (op) => op !== 'unassign' || hasCustodian,
  );

  // The current category stays selectable even if it has since been retired.
  const editableCategories = (categories.data ?? []).filter(
    (row) => row.id === data.categoryId || (row.status === 'ACTIVE' && !row.consumable),
  );

  const operationReady = (() => {
    switch (operation) {
      case 'assign':
        return assignTarget === 'EMPLOYEE' ? employee !== null : departmentId !== '';
      case 'move':
        return opLocationId !== '';
      case 'condition':
      case 'completeMaintenance':
        return opCondition !== '';
      case 'found':
        return opLocationId !== '' && opCondition !== '';
      case 'lost':
      case 'dispose':
        return opReason.trim() !== '';
      default:
        return true;
    }
  })();

  const locationSelect = (label: string, required: boolean, emptyLabel: string, hint?: string) => (
    <SelectField
      label={label}
      value={opLocationId}
      onChange={(event) => setOpLocationId(event.target.value)}
      required={required}
      hint={locations.isError ? 'Locations could not be loaded.' : hint}
    >
      <option value="">{locations.isPending ? 'Loading…' : emptyLabel}</option>
      {activeLocations.map((row) => (
        <option key={row.id} value={row.id}>
          {locationLabel(row)}
        </option>
      ))}
    </SelectField>
  );

  const conditionSelect = (label: string) => (
    <SelectField
      label={label}
      value={opCondition}
      onChange={(event) => setOpCondition(event.target.value as AssetCondition | '')}
      required
    >
      <option value="">Choose a condition</option>
      {ASSET_CONDITIONS.map((value) => (
        <option key={value} value={value}>
          {humanise(value)}
        </option>
      ))}
    </SelectField>
  );

  const notesField = (
    <TextField
      label="Notes"
      value={opNotes}
      onChange={(event) => setOpNotes(event.target.value)}
      maxLength={1000}
    />
  );

  return (
    <div>
      <Link
        to={ROUTES.assets}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Assets
      </Link>

      <PageHeader
        title={data.name}
        description={`${data.assetTag}${data.categoryName === undefined ? '' : ` · ${data.categoryName}`}`}
        icon={<Package className="h-5 w-5" aria-hidden="true" />}
        action={
          <span className="flex flex-wrap items-center gap-2">
            <AssetStatusBadge status={data.status} />
            {manages && data.status !== 'DISPOSED' && (
              <Button size="sm" variant="secondary" onClick={() => openEdit(data)}>
                Edit details
              </Button>
            )}
          </span>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Custody</h2>
            <dl className="mt-2">
              <Field label="Status">
                <AssetStatusBadge status={data.status} />
              </Field>
              <Field label="Location">{data.locationName ?? 'No location'}</Field>
              <Field label="Custodian">
                {data.custodianEmployeeName ??
                  (data.custodianDepartmentName === undefined
                    ? 'Unassigned'
                    : `${data.custodianDepartmentName} (department)`)}
              </Field>
              <Field label="Condition">
                <AssetConditionBadge condition={data.condition} />
              </Field>
              {data.statusReason !== undefined && <Field label="Reason">{data.statusReason}</Field>}
              {data.disposedAt !== undefined && (
                <Field label="Disposed">{formatDateTime(data.disposedAt)}</Field>
              )}
            </dl>
          </Card>

          {manages && (
            <Card className="p-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Operations
              </h2>
              {available.length === 0 ? (
                <p className="mt-2 text-sm text-muted">
                  Disposed of — the record and its history remain, and nothing more can happen to it.
                </p>
              ) : (
                <>
                  <p className="mt-0.5 text-xs text-muted">
                    What a {humanise(data.status)} asset allows. Each is written to the history.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {available.map((op) => (
                      <Button
                        key={op}
                        size="sm"
                        variant={DANGEROUS.includes(op) ? 'danger' : 'secondary'}
                        onClick={() => openOperation(op)}
                      >
                        {OPERATION_LABEL[op]}
                      </Button>
                    ))}
                  </div>
                </>
              )}
            </Card>
          )}

          <Card className="p-4">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">Details</h2>
              <WarrantyBadge state={data.warrantyState} />
            </div>
            <dl className="mt-2">
              <Field label="Tag">
                <span className="font-mono">{data.assetTag}</span>
              </Field>
              <Field label="Category">{orDash(data.categoryName)}</Field>
              <Field label="Serial">{orDash(data.serialNumber)}</Field>
              <Field label="Manufacturer">{orDash(data.manufacturer)}</Field>
              <Field label="Model">{orDash(data.model)}</Field>
              <Field label="Vendor">{orDash(data.vendor)}</Field>
              <Field label="Purchased">{formatDate(data.purchaseDate)}</Field>
              <Field label="Cost">
                <Money value={data.purchaseCost} />
              </Field>
              <Field label="Warranty until">{formatDate(data.warrantyExpiresOn)}</Field>
              <Field label="Registered">{formatDateTime(data.createdAt)}</Field>
            </dl>
            {data.notes !== undefined && (
              <p className="mt-3 whitespace-pre-line break-words border-t border-line pt-3 text-sm text-muted">
                {data.notes}
              </p>
            )}
          </Card>
        </div>

        <Card>
          <CardHeader
            title="History"
            description="Everything that ever happened to this asset, newest first."
          />
          <div className="px-5 pb-4">
            {history.isPending ? (
              <LoadingState label="Loading history" />
            ) : history.error !== null ? (
              <ErrorState error={history.error} onRetry={() => void history.refetch()} />
            ) : (history.data ?? []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">Nothing recorded yet.</p>
            ) : (
              <ol className="divide-y divide-line">
                {(history.data ?? []).map((event) => (
                  <HistoryEntry key={event.id} event={event} />
                ))}
              </ol>
            )}
          </div>
        </Card>
      </div>

      <Modal
        open={operation !== null && !confirming}
        onClose={closeOperation}
        title={operation === null ? '' : `${OPERATION_LABEL[operation]} · ${data.assetTag}`}
        description={operation === null ? undefined : OPERATION_DESCRIPTION[operation]}
        footer={
          <>
            <Button variant="secondary" onClick={closeOperation} disabled={operate.isPending}>
              Cancel
            </Button>
            <Button
              variant={operation !== null && DANGEROUS.includes(operation) ? 'danger' : 'primary'}
              loading={operate.isPending}
              disabled={!operationReady}
              onClick={() => {
                if (operation === null) {
                  return;
                }
                if (DANGEROUS.includes(operation)) {
                  setConfirming(true);
                } else {
                  operate.mutate(operation);
                }
              }}
            >
              {operation === null ? 'Save' : OPERATION_LABEL[operation]}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {operation === 'assign' && (
            <>
              {hasCustodian && (
                <p className="text-sm text-muted">
                  Currently with{' '}
                  <span className="text-ink">
                    {data.custodianEmployeeName ?? data.custodianDepartmentName}
                  </span>
                  .
                </p>
              )}
              {seesDepartments && (
                <div role="group" aria-label="Assign to" className="grid grid-cols-2 gap-2">
                  {(
                    [
                      { value: 'EMPLOYEE', label: 'A person' },
                      { value: 'DEPARTMENT', label: 'A department' },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setAssignTarget(option.value)}
                      aria-pressed={assignTarget === option.value}
                      className={`border px-3 py-2 text-sm font-semibold ${
                        assignTarget === option.value
                          ? 'border-accent bg-accent-soft text-accent-soft-fg'
                          : 'border-line bg-bg text-muted'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
              {assignTarget === 'EMPLOYEE' || !seesDepartments ? (
                <EmployeePicker value={employee} onChange={setEmployee} label="Assign to" />
              ) : (
                <SelectField
                  label="Department"
                  value={departmentId}
                  onChange={(event) => setDepartmentId(event.target.value)}
                  required
                  hint={
                    departments.isError
                      ? 'Departments could not be loaded.'
                      : !departments.isPending && (departments.data ?? []).length === 0
                        ? 'No active departments.'
                        : undefined
                  }
                >
                  <option value="">{departments.isPending ? 'Loading…' : 'Choose a department'}</option>
                  {(departments.data ?? []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.campusName === undefined ? row.name : `${row.name} · ${row.campusName}`}
                    </option>
                  ))}
                </SelectField>
              )}
              {!seesDepartments && (
                <p className="text-xs text-subtle">
                  Assigning to a department needs permission to view departments.
                </p>
              )}
              {locationSelect('Move to', false, 'Leave where it is')}
              {notesField}
            </>
          )}

          {operation === 'unassign' && (
            <>
              {locationSelect('Put it in', false, 'Leave where it is')}
              {notesField}
            </>
          )}

          {operation === 'move' && (
            <>
              <p className="text-sm text-muted">
                Now at <span className="text-ink">{data.locationName ?? 'no location'}</span>.
              </p>
              {locationSelect('New location', true, 'Choose a location')}
              {notesField}
            </>
          )}

          {operation === 'condition' && (
            <>
              {conditionSelect('Condition now')}
              {notesField}
            </>
          )}

          {operation === 'startMaintenance' && notesField}

          {operation === 'completeMaintenance' && (
            <>
              {conditionSelect('Came back in')}
              <TextField
                label="Repair cost"
                type="number"
                min={0}
                step="0.01"
                value={opCost}
                onChange={(event) => setOpCost(event.target.value)}
                hint="Recorded on the history, not the ledger."
              />
              {notesField}
            </>
          )}

          {operation === 'found' && (
            <>
              {locationSelect('Found at', true, 'Choose a location')}
              {conditionSelect('Condition')}
              {notesField}
            </>
          )}

          {(operation === 'lost' || operation === 'dispose') && (
            <TextField
              label="Reason"
              value={opReason}
              onChange={(event) => setOpReason(event.target.value)}
              required
              hint={
                operation === 'dispose'
                  ? 'Sold, scrapped, donated, written off — kept on the record for good.'
                  : 'Where and when it was last seen, if known.'
              }
            />
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={operation !== null && confirming}
        destructive
        title={
          operation === 'dispose'
            ? `Dispose of ${data.assetTag}?`
            : `Report ${data.assetTag} as lost?`
        }
        message={
          operation === 'dispose'
            ? `Disposal is final. ${data.name} stays on the record with its history, but it can never be assigned, moved or found again. Reason: "${opReason.trim()}".`
            : `${data.name} will be recorded as lost, and its custody and location will no longer be trusted until it is found. Reason: "${opReason.trim()}".`
        }
        confirmLabel={operation === 'dispose' ? 'Dispose' : 'Report lost'}
        loading={operate.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          if (operation !== null) {
            operate.mutate(operation);
          }
        }}
      />

      <Modal
        open={editing}
        onClose={() => setEditing(false)}
        title={`Edit ${data.assetTag}`}
        description="Descriptive details only. Location, custody, condition and status change through the operations, so the history records them."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={name.trim() === '' || categoryId === ''}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={200}
            required
          />
          <SelectField
            label="Category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            required
            hint={categories.isError ? 'Categories could not be loaded.' : undefined}
          >
            {categories.isPending && <option value={data.categoryId}>{data.categoryName ?? 'Loading…'}</option>}
            {editableCategories.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </SelectField>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Serial number"
              value={serialNumber}
              onChange={(event) => setSerialNumber(event.target.value)}
              maxLength={100}
            />
            <TextField
              label="Vendor"
              value={vendor}
              onChange={(event) => setVendor(event.target.value)}
              maxLength={200}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Manufacturer"
              value={manufacturer}
              onChange={(event) => setManufacturer(event.target.value)}
              maxLength={150}
            />
            <TextField
              label="Model"
              value={model}
              onChange={(event) => setModel(event.target.value)}
              maxLength={150}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <TextField
              label="Purchased on"
              type="date"
              value={purchaseDate}
              onChange={(event) => setPurchaseDate(event.target.value)}
            />
            <TextField
              label="Purchase cost"
              type="number"
              min={0}
              step="0.01"
              value={purchaseCost}
              onChange={(event) => setPurchaseCost(event.target.value)}
            />
            <TextField
              label="Warranty until"
              type="date"
              value={warrantyExpiresOn}
              onChange={(event) => setWarrantyExpiresOn(event.target.value)}
            />
          </div>
          <TextField
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            maxLength={1000}
          />
        </div>
      </Modal>
    </div>
  );
}
